import type { CSSProperties, ReactNode } from "react";

import { Select } from "@/components/inputs";

export type CompanyOption = { company_id: string; name: string };

/** THE company filter. Every module that offers one renders THIS.
 *
 *  WHY IT EXISTS. Registry, Asset Data and FOTA each grew their own company
 *  dropdown, and each prepended its own "All companies" option. All three
 *  servers scope correctly — a company_admin's list comes back with exactly
 *  their own company in it — so no data ever leaked. But the CONTROL said
 *  otherwise: "All companies" told someone who can only ever see one company
 *  that the platform has others and they are browsing across them. Reported as
 *  a suspected leak, which is the expensive kind of wrong: it costs an
 *  investigation to establish that nothing is broken.
 *
 *  THE RULE: a chooser with nothing to choose between is not a chooser. When
 *  the caller can see one company, this renders that company as text. When they
 *  can see several — a platform operator — it renders the full filter.
 *
 *  IT KEYS OFF `scoped`, NOT OFF LENGTH, and that correction cost a real bug.
 *
 *  The first version collapsed whenever ONE company came back, treating that as
 *  "this caller can only see themselves". It is not. One row means two
 *  different things:
 *
 *    scoped   -> you are a customer, filtered to yourself. Collapse: there is
 *                nothing to choose, and `extra` (FOTA's allocated/unallocated
 *                buckets) matches zero rows for you by construction.
 *    unscoped -> there is ONE COMPANY ON THE PLATFORM. You are an operator who
 *                can see across all of them; there just happens to be one.
 *
 *  Collapsing the second case hid the `extra` options from the only caller who
 *  can use them, so a platform operator could not filter FOTA to UNALLOCATED
 *  devices -- the stock-before-it-is-sold case, which is most of what FOTA is
 *  for. It also read as "you have been pinned to this company", which is how it
 *  was reported.
 *
 *  Only the SERVER knows which case holds, because only the server knows
 *  whether it filtered. So it says so, and `scoped` is REQUIRED here for the
 *  same reason `viewer` is required in the services: a default would silently
 *  pick one meaning and be wrong half the time.
 */
export function CompanyFilter({
  value, onChange, companies, scoped, label = "Company", allLabel = "All companies",
  extra, style, width = 180, fill = false,
}: {
  value: string;
  onChange: (v: string) => void;
  companies: CompanyOption[];
  /** Did the SERVER filter this list to the caller? Required on purpose --
   *  see the note above. Comes straight from the endpoint that returned
   *  `companies`, never from a role check in the console. */
  scoped: boolean;
  label?: string | null;
  /** The "no filter" row. `null` removes it — for a picker that must name one. */
  allLabel?: string | null;
  /** Cross-company-only options (FOTA's allocated / unallocated buckets).
   *  Hidden alongside the filter itself, because they are answers to "whose?"
   *  and a single-company caller has already answered it. */
  extra?: ReactNode;
  style?: CSSProperties;
  width?: number;
  /** Stretch to the container instead of a fixed `width`, for a caller whose
   *  siblings are `className="fill"` selects in a grid. Added rather than
   *  letting a caller pass `width={0}` — which is what Fleet did, and it
   *  rendered an Owner control with no width at all. */
  fill?: boolean;
}) {
  // Collapse only when the list is BOTH filtered and singular. An unscoped
  // caller keeps the full control even at length 1, because their one entry is
  // the platform's only company rather than a cage, and because `extra` is
  // theirs alone to use.
  const only = scoped && companies.length === 1 ? companies[0] : null;

  if (only) {
    return (
      <div style={style}>
        {label && <label className="simlab">{label}</label>}
        {/* Shown rather than omitted: the company is real context - which
            fleet you are looking at - and removing it entirely would leave a
            page that never says whose data it is. */}
        <div className="cap" style={{ paddingTop: 6, minWidth: width }}>{only.name}</div>
      </div>
    );
  }

  return (
    <div style={style}>
      {label && <label className="simlab">{label}</label>}
      <Select className={fill ? "fill" : undefined}
              style={fill ? { minWidth: 0 } : { width, minWidth: 0 }} value={value}
              onChange={(e) => onChange(e.target.value)}>
        {allLabel !== null && <option value="">{allLabel}</option>}
        {extra}
        {companies.map((c) => (
          <option key={c.company_id} value={c.company_id}>{c.name}</option>
        ))}
      </Select>
    </div>
  );
}
